/**
 * Work / launch / investment types → calendar predictability (Slice 4).
 * Profile is a mixin — not a work-type column — so launch/investment types can share it.
 */

import type { PlacementAnchoring } from './placementAnchoring';
import { resolveAnchoring } from './placementAnchoring';

/** @deprecated Use PlacementAnchoring */
export type LaunchFlexibility = PlacementAnchoring;

export interface PredictabilityProfile {
  /** Multiplier applied as schedule slack (1 = default week band). */
  uncertainty_factor: number;
  /** Max calendar confidence (0–1) a placement of this type can carry. */
  confidence_ceiling: number;
  /**
   * Higher = firmer. Firmer types displace softer ones in conflicts; never the reverse.
   * Fixed anchors (Slice 3) still never slide regardless of firmness.
   */
  commitment_firmness: number;
  /** Derived weeks of soft slip band from uncertainty_factor. */
  slackWeeks: number;
  /** Starting calendar confidence before unconfirmed-assumption penalties. */
  calendarConfidence: number;
  suggestedFlexibility: PlacementAnchoring;
  rationale: string;
}

/** Workspace-overridable registry key → profile. Defaults ship in code. */
export type PredictabilityProfileKey = string;

const DEFAULT_PROFILES: Record<string, Omit<PredictabilityProfile, 'slackWeeks' | 'calendarConfidence' | 'suggestedFlexibility' | 'rationale'> & { suggestedFlexibility: PlacementAnchoring; rationale: string }> = {
  experimental: {
    uncertainty_factor: 2.5,
    confidence_ceiling: 0.45,
    commitment_firmness: 1,
    suggestedFlexibility: 'flexible',
    rationale: 'Experimental / spike work — high slack, low confidence, slides first',
  },
  solid: {
    uncertainty_factor: 1,
    confidence_ceiling: 0.9,
    commitment_firmness: 8,
    suggestedFlexibility: 'fixed',
    rationale: 'Firm commitment type — high confidence, slides last',
  },
  default: {
    uncertainty_factor: 1.25,
    confidence_ceiling: 0.75,
    commitment_firmness: 4,
    suggestedFlexibility: 'flexible',
    rationale: 'Default: movable window with modest slack',
  },
};

const EXPERIMENTAL_WORK = new Set([
  'spike',
  'experiment',
  'research',
  'prototype',
  'discovery',
  'poc',
  'r_and_d',
  'r&d',
]);

const FIRM_INVESTMENT = new Set([
  'customer_commitment',
  'committed',
  'contractual',
  'regulatory',
  'compliance',
]);

const FIRM_LAUNCH = new Set([
  'ga',
  'general_availability',
  'major_release',
  'milestone',
  'product_launch',
  'pricing_packaging',
]);

function norm(v?: string | null): string {
  return (v ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
}

function finish(
  partial: (typeof DEFAULT_PROFILES)[string],
): PredictabilityProfile {
  const slackWeeks = Math.max(0, Math.round((partial.uncertainty_factor - 1) * 2));
  return {
    ...partial,
    slackWeeks,
    calendarConfidence: partial.confidence_ceiling,
  };
}

/**
 * Derive predictability mixin from taxonomy. Workspace overrides can replace
 * DEFAULT_PROFILES later without changing this call shape.
 */
export function derivePredictability(input: {
  workType?: string | null;
  launchType?: string | null;
  investmentType?: string | null;
  /** Optional workspace profile overrides keyed by 'experimental' | 'solid' | 'default'. */
  workspaceProfiles?: Partial<typeof DEFAULT_PROFILES>;
}): PredictabilityProfile {
  const work = norm(input.workType);
  const launch = norm(input.launchType);
  const invest = norm(input.investmentType);
  const registry = { ...DEFAULT_PROFILES, ...input.workspaceProfiles };

  const experimental =
    EXPERIMENTAL_WORK.has(work) || work.includes('experiment') || work.includes('spike');
  const firmInvest = FIRM_INVESTMENT.has(invest) || invest.includes('commitment');
  const firmLaunch = FIRM_LAUNCH.has(launch);

  if (firmInvest || firmLaunch) {
    const base = finish(registry.solid);
    if (experimental) {
      return {
        ...base,
        uncertainty_factor: Math.max(base.uncertainty_factor, 1.5),
        slackWeeks: 1,
        confidence_ceiling: Math.min(base.confidence_ceiling, 0.7),
        calendarConfidence: Math.min(base.calendarConfidence, 0.7),
        commitment_firmness: base.commitment_firmness,
        rationale: firmInvest
          ? `Investment type “${input.investmentType}” is firm; work type still adds modest slack`
          : `Launch type “${input.launchType}” is firm; work type still adds modest slack`,
      };
    }
    return {
      ...base,
      rationale: firmInvest
        ? `Investment type “${input.investmentType}” implies a firm commitment date`
        : `Launch type “${input.launchType}” implies an immovable launch anchor`,
    };
  }

  if (experimental) {
    return finish(registry.experimental);
  }

  return finish(registry.default);
}

export function resolveLaunchFlexibility(
  explicit: PlacementAnchoring | null | undefined,
  suggested: PlacementAnchoring,
): PlacementAnchoring {
  return resolveAnchoring(explicit, suggested);
}

/** Sort key for placement order: firmer first (lower index = place earlier / keep). Soft slides first when displacing. */
export function commitmentFirmnessOf(priority: {
  commitmentFirmness?: number;
  launchFlexibility?: PlacementAnchoring;
}): number {
  if ((priority.launchFlexibility ?? 'flexible') === 'fixed') return 10_000;
  return priority.commitmentFirmness ?? 4;
}

export { DEFAULT_PROFILES as BUILTIN_PREDICTABILITY_PROFILES };
